import { createPortal } from 'react-dom';
import { useRef, useState } from 'react';
import { taxKindOf } from '@progear/finance-core';
import {
  useCashFlowEntryDetail,
  useMarkCashFlowEntryPaid,
  useRemoveManualPayments,
  useUpdateCashFlowEntry,
  useUpdateCashFlowEntryOverride,
  useLinkPrepayment,
  useUnlinkPrepayment,
} from '@/hooks/useCashFlow';
import type { CashFlowEntry, CashFlowRequestOptions, MoneyMode } from '@/lib/cashFlow';
import type { CashFlowOffer } from '@/lib/cashFlowDetail';
import { EntryFactsPane } from './EntryFactsPane';
import { LoanInstallmentSummary } from './LoanInstallmentSummary';
import { LoanInvoiceLinkSection } from './LoanInvoiceLinkSection';
import { PaymentPlanSummary } from './PaymentPlanSummary';
import { EnrichmentForm } from './EnrichmentForm';
import { DocumentPane } from './DocumentPane';
import { RelatedDocuments } from '../RelatedDocuments';
import { OfferSummary } from '../OfferSummary';
import { OfferViewer } from '../OfferViewer';
import { LinkPrepaymentPicker } from './LinkPrepaymentPicker';
import { kmdDeclarationMonths, kmdDeclarationScope } from '../kmdScope';
import type { LedgerScope } from '../Ledger';
import { ReconciliationPanel } from './ReconciliationPanel';
import { KmdSettlementPanel } from './KmdSettlementPanel';

/**
 * RIKASTATUD KIRJE / DOKUMENDIVAADE (module 1.3) — the one drill surface shared by
 * BOTH cash-flow lenses (L14). It is a full-screen overlay (portalled to
 * document.body so a backdrop-filter/transform ancestor can never re-anchor the
 * fixed positioning) with two panes: LEFT = facts + settlement + editable
 * enrichment, RIGHT = the source document inline. All state (detail, override,
 * comment) flows through the shared query layer — never lens-local. Money is read
 * from finance-core (L9); this component computes nothing.
 */
export interface EnrichedEntryDetailProps {
  entryId: string;
  today: string;
  onClose: () => void;
  mode?: MoneyMode;
  requestOptions?: CashFlowRequestOptions;
  /** Hard-delete THIS entry — offered only for a manual entry (the server guards the
   *  rest with a 409). On click the detail deletes, then closes. The same destructive
   *  action the register row exposes; reachable from every lens via this shared drill
   *  surface (L14). */
  onDelete?: (id: string) => void;
  /** Open ANOTHER entry's detail from a related-document link — lets the linked
   *  ETT ↔ ARV pair navigate to each other from this one shared drill (L14). */
  onOpenEntry?: (id: string) => void;
  /** The window's entries — the candidate pool for the "Seo ettemaksuga" picker
   *  (same party + kind prepayments). The picker filters this; it never fetches
   *  its own, so the action reads the SAME shared data as every lens (L14). */
  candidatePrepayments?: CashFlowEntry[];
  /** Open the scoped register (Ledger) — on a materialized KMD entry the detail
   *  offers "Ava deklaratsiooni dokumendid", the SAME declaration drill the table
   *  lens's KMD row exposes (shared `kmdDeclarationScope` builder — L14: the
   *  calendar chip reaches the function through this shared detail). */
  onOpenScope?: (scope: LedgerScope) => void;
}

/** Width bounds (px) for the LEFT facts pane. The default is 30rem (480px) — a step
 *  wider than the approved canvas 26rem, per user feedback (the settlement block was
 *  cramped); the drag divider lets the user trade facts width for document width
 *  without ever collapsing either pane. Transient LAYOUT state only (a view
 *  preference) — never finance state, so it lives locally, not in Supabase. */
const FACTS_PANE_DEFAULT_PX = 480;
const FACTS_PANE_MIN_PX = 320;
const FACTS_PANE_MAX_PX = 672;
const FACTS_PANE_KEY_STEP_PX = 16;

const clampFactsPaneWidth = (px: number) =>
  Math.min(FACTS_PANE_MAX_PX, Math.max(FACTS_PANE_MIN_PX, px));

export function EnrichedEntryDetail({
  entryId,
  today,
  onClose,
  mode = 'gross',
  requestOptions,
  onDelete,
  onOpenEntry,
  candidatePrepayments,
  onOpenScope,
}: EnrichedEntryDetailProps) {
  const { data, isLoading } = useCashFlowEntryDetail(entryId, requestOptions);
  const override = useUpdateCashFlowEntryOverride(requestOptions);
  const comment = useUpdateCashFlowEntry(requestOptions);
  const link = useLinkPrepayment(requestOptions);
  const unlink = useUnlinkPrepayment(requestOptions);
  // Manual "Märgi laekunuks/tasutuks" from the Arveldus section — the detail lens's
  // mount of the ONE shared mark-paid function (L14). The key is minted per click so
  // a React-Query retry re-applies the SAME settlement (at-least-once safe).
  const markPaid = useMarkCashFlowEntryPaid(requestOptions);
  // "Tühista käsitsi makse" — the mark-paid UNDO (retracts the Manuaal settlement
  // events after a mis-click). Naturally idempotent DELETE — no key to mint. Both
  // lenses reach it through this ONE shared detail (L14).
  const removeManual = useRemoveManualPayments(requestOptions);
  const isSaving = override.isPending || comment.isPending;
  const [viewingOffer, setViewingOffer] = useState<CashFlowOffer | null>(null);
  // Resizable split: the LEFT pane's width in px; the divider drags it (pointer
  // capture keeps the drag alive outside the 6px handle) or nudges it by keyboard.
  const [factsWidth, setFactsWidth] = useState(FACTS_PANE_DEFAULT_PX);
  const drag = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null);

  return createPortal(
    <div
      data-testid="enriched-entry-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Rikastatud kirje"
      onClick={onClose}
      className="fixed inset-0 z-50 flex bg-background/70 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="m-auto flex h-[92vh] w-[min(110rem,98vw)] overflow-hidden rounded-lg border border-border bg-background shadow-2xl"
      >
        {data ? (
          <>
            <div
              data-testid="enriched-facts-pane"
              style={{ width: factsWidth }}
              className="flex shrink-0 flex-col gap-3 overflow-auto p-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground">Rikastatud kirje · dokumendivaade</h2>
                <div className="flex items-center gap-1.5">
                  {onDelete && data.entry.source === 'manual' && (
                    <button
                      type="button"
                      onClick={() => {
                        onDelete(entryId);
                        onClose();
                      }}
                      className="rounded border border-[hsl(var(--cost))]/50 px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--cost))] transition-colors hover:bg-[hsl(var(--cost))]/10"
                    >
                      Kustuta kirje
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label="Sulge"
                    onClick={onClose}
                    className="rounded border border-border px-2 py-0.5 text-muted-foreground hover:bg-accent"
                  >
                    <span aria-hidden>×</span>
                  </button>
                </div>
              </div>
              <EntryFactsPane
                entry={data.entry}
                today={today}
                mode={mode}
                options={requestOptions}
                onMarkPaid={(intent) =>
                  markPaid.mutate({
                    id: entryId,
                    input: { ...intent, idempotencyKey: crypto.randomUUID() },
                  })
                }
                markPaidPending={markPaid.isPending}
                onRemoveManual={() => removeManual.mutate({ id: entryId })}
                removeManualPending={removeManual.isPending}
              />
              <KmdSettlementPanel entry={data.entry} today={today} options={requestOptions} />
              <ReconciliationPanel entry={data.entry} options={requestOptions} onOpenEntry={onOpenEntry} />
              <LoanInstallmentSummary
                entry={data.entry}
                installment={data.loanInstallment}
                mode={mode}
                onOpenEntry={onOpenEntry}
              />
              <LoanInvoiceLinkSection
                entry={data.entry}
                requestOptions={requestOptions}
                onOpenEntry={onOpenEntry}
              />
              {/* KMD deklaratsiooni drill — the materialized declaration entry
                  links to its CONTRIBUTING documents (the accrual month's
                  VAT-bearing mirror rows) through the shared scope builder. */}
              {onOpenScope !== undefined && taxKindOf(data.entry) === 'KMD' && (
                <button
                  type="button"
                  onClick={() =>
                    onOpenScope(kmdDeclarationScope(kmdDeclarationMonths([data.entry]), 'dokumendid'))
                  }
                  className="rounded border border-border px-2.5 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  Ava deklaratsiooni dokumendid →
                </button>
              )}
              {data.entry.source === 'payment_plan' && (
                <PaymentPlanSummary
                  entry={data.entry}
                  mode={mode}
                  onOpenEntry={onOpenEntry}
                  requestOptions={requestOptions}
                />
              )}
              <RelatedDocuments
                entry={data.entry}
                onUnlink={(linkId) => unlink.mutate(linkId)}
                onOpenRelated={onOpenEntry}
              />
              <OfferSummary offer={data.offer} onOpen={() => setViewingOffer(data.offer)} />
              <LinkPrepaymentPicker
                final={data.entry}
                entries={candidatePrepayments ?? []}
                isLinking={link.isPending}
                onLink={(input) =>
                  link.mutate({
                    finalId: entryId,
                    input: { ...input, idempotencyKey: crypto.randomUUID() },
                  })
                }
              />
              <EnrichmentForm
                detail={data}
                isSaving={isSaving}
                onSaveOverride={(patch) => override.mutate({ id: entryId, patch })}
                onSaveComment={(value) => comment.mutate({ id: entryId, patch: { comment: value } })}
                requestOptions={requestOptions}
              />
            </div>
            {/* Drag divider between the panes (also the visual seam — it owns the
                border the left pane used to carry). Pointer-captured drag + arrow-key
                nudge; clamped so neither pane can be collapsed away. */}
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Muuda paanide laiust"
              aria-valuemin={FACTS_PANE_MIN_PX}
              aria-valuemax={FACTS_PANE_MAX_PX}
              aria-valuenow={factsWidth}
              tabIndex={0}
              onPointerDown={(e) => {
                drag.current = { pointerId: e.pointerId, startX: e.clientX, startWidth: factsWidth };
                // Keeps move/up events flowing to the handle even when the cursor
                // leaves its 6px strip mid-drag (jsdom has no pointer capture — optional).
                e.currentTarget.setPointerCapture?.(e.pointerId);
              }}
              onPointerMove={(e) => {
                if (drag.current === null || drag.current.pointerId !== e.pointerId) return;
                setFactsWidth(clampFactsPaneWidth(drag.current.startWidth + (e.clientX - drag.current.startX)));
              }}
              onPointerUp={() => {
                drag.current = null;
              }}
              onPointerCancel={() => {
                drag.current = null;
              }}
              onKeyDown={(e) => {
                if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
                e.preventDefault();
                const step = e.key === 'ArrowRight' ? FACTS_PANE_KEY_STEP_PX : -FACTS_PANE_KEY_STEP_PX;
                setFactsWidth((width) => clampFactsPaneWidth(width + step));
              }}
              className="group flex w-1.5 shrink-0 cursor-col-resize justify-center outline-none hover:bg-[hsl(var(--primary))]/15 focus-visible:bg-[hsl(var(--primary))]/20"
            >
              <span aria-hidden className="w-px bg-border group-hover:bg-[hsl(var(--primary))]/60" />
            </div>
            <DocumentPane document={data.document} entryId={entryId} requestOptions={requestOptions} />
            {viewingOffer !== null && (
              <OfferViewer offer={viewingOffer} onClose={() => setViewingOffer(null)} />
            )}
          </>
        ) : isLoading ? (
          <div className="m-auto text-[13px] text-muted-foreground">Laadin kirjet…</div>
        ) : (
          <div className="m-auto flex flex-col items-center gap-3 text-center">
            <p className="text-[13px] text-muted-foreground">Kirje laadimine ebaõnnestus.</p>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-3 py-1.5 text-[12px] text-muted-foreground hover:bg-accent"
            >
              Sulge
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
